import { useId } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/Card";
import { fmtDate } from "@/lib/dates";
import type { GoalAssessment } from "@/types";

const REASONS = new Set([
  "missing_goal", "missing_training_days", "missing_training_time", "missing_training_sports",
  "no_recent_training", "incomplete_training_data", "goal_date_passed", "capacity_below_declared_hours",
  "race_sports_missing", "availability_below_reference", "goal_time_requires_review",
]);
const PRIMARY_GOALS = new Set([
  "complete_ironman_70_3", "complete_ironman_full", "marathon_sub_4", "marathon_sub_3_30",
  "general_fitness", "weight_management", "injury_recovery", "race_specific_training",
  "performance_plateau", "complete_ironman_70_3_oct_2026",
]);
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

/** Only the two reviewed public examples can become external links. */
function referenceUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && ["ironman.com", "www.ironman.com"].includes(url.hostname)
      && !url.username && !url.password && !url.port && !url.search && !url.hash
      && ["/news/how-do-i-know-if-im-ready-ironman-703", "/news/calling-all-rookies"].includes(url.pathname.replace(/\/$/, ""))
      ? url.href : null;
  } catch { return null; }
}

export function GoalAssessmentCard({ assessment }: { assessment?: GoalAssessment | null }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  if (!assessment) return null;
  const number = (value: number | null | undefined, digits = 1) =>
    typeof value === "number" && Number.isFinite(value) && value >= 0
      ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: digits }).format(value)
      : t("goal_assessment.not_set");
  const hours = (value: number | null | undefined) => value == null || !Number.isFinite(value) || value < 0
    ? t("goal_assessment.not_set") : t("goal_assessment.hours_value", { hours: number(value) });
  const date = (value: string | null | undefined) => {
    if (!value) return t("goal_assessment.not_set");
    try { return fmtDate(value, "d MMM yyyy"); } catch { return t("goal_assessment.not_set"); }
  };
  const seconds = assessment.goal?.goal_time_seconds;
  const desiredTime = typeof seconds === "number" && Number.isSafeInteger(seconds) && seconds > 0
    ? [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].map((part, index) => index ? String(part).padStart(2, "0") : String(part)).join(":")
    : t("goal_assessment.not_set");
  const status = ["needs_information", "review_required", "no_obvious_conflict"].includes(assessment.status)
    ? assessment.status : "needs_information";
  const reasons = [...new Set(assessment.reasons.map(reason => REASONS.has(reason) ? reason : "unknown_reason"))];
  const recorded = assessment.recorded_28d;
  const params = {
    declared: number(assessment.declared_weekly_hours), capacity: number(assessment.selected_days_capacity_hours),
    effective: number(assessment.effective_weekly_hours), from: number(assessment.reference?.weekly_hours_from),
    to: number(assessment.reference?.weekly_hours_to),
    date: date(assessment.goal?.date),
  };
  const example = assessment.reference?.kind === "published_example" ? assessment.reference : null;
  const source = example ? referenceUrl(example.source_url) : null;
  const primaryGoals = assessment.primary_goals.filter(goal => typeof goal === "string" && goal.trim())
    .map(goal => PRIMARY_GOALS.has(goal) ? t(`onboarding.step.goals.goal.${goal}`) : goal);
  const days = [...new Set(assessment.available_days)].filter(day => Number.isInteger(day) && day >= 0 && day <= 6).sort((a, b) => a - b);

  return <Card role="region" aria-labelledby={titleId} className="min-w-0 border-amber/40">
    <h2 id={titleId} className="font-serif text-xl font-medium">{t("goal_assessment.title")}</h2>
    <p className="mt-2 font-medium">{t(`goal_assessment.${status}`)}</p>
    <p className="mt-1 text-sm text-taupe">{t("goal_assessment.scope_note")}</p>
    <dl className="mt-4 grid min-w-0 grid-cols-1 gap-x-5 gap-y-3 text-sm sm:grid-cols-2">
      <div className="min-w-0"><dt className="text-taupe">{t("goal_assessment.event")}</dt><dd className="break-words font-medium">{assessment.goal?.name || primaryGoals.join(", ") || t("goal_assessment.not_set")}</dd></div>
      <div><dt className="text-taupe">{t("goal_assessment.date")}</dt><dd>{date(assessment.goal?.date)}</dd></div>
      <div><dt className="text-taupe">{t("goal_assessment.goal_time")}</dt><dd>{desiredTime}</dd></div>
      <div><dt className="text-taupe">{t("goal_assessment.available_days")}</dt><dd>{days.map(day => t(`weekday.${WEEKDAYS[day]}_long`)).join(", ") || t("goal_assessment.not_set")}</dd></div>
      <div><dt className="text-taupe">{t("goal_assessment.declared_hours")}</dt><dd>{hours(assessment.declared_weekly_hours)}</dd></div>
      <div><dt className="text-taupe">{t("goal_assessment.capacity_hours")}</dt><dd>{hours(assessment.selected_days_capacity_hours)}</dd></div>
    </dl>
    {assessment.goal?.name && primaryGoals.length > 0 && <p className="mt-3 break-words text-sm">{t("onboarding.step.goals.goals_label")}: {primaryGoals.join(", ")}</p>}
    {assessment.goal?.goal_notes && <p className="mt-3 whitespace-pre-wrap break-words text-sm">{assessment.goal.goal_notes}</p>}
    {reasons.length > 0 && <ul className="mt-4 list-disc space-y-2 pl-5 text-sm">
      {reasons.map(reason => <li key={reason}>{t(`goal_assessment.${reason}`, params)}</li>)}
    </ul>}
    <div className="mt-4 border-t border-cream-border pt-3 text-sm">
      <h3 className="font-medium">{t("goal_assessment.recorded_training")}</h3>
      <p className="mt-1">{t("goal_assessment.coverage", {
        workouts: number(recorded.workouts, 0), days: number(recorded.active_days, 0),
        hours: number(typeof recorded.duration_minutes === "number" ? recorded.duration_minutes / 60 : null),
      })}</p>
      <p className="mt-1 text-taupe">{date(recorded.start_date)} – {date(recorded.end_date)}</p>
      <p className="mt-1 text-taupe">{t("goal_assessment.coverage_note")}</p>
    </div>
    {example && <div className="mt-4 border-t border-cream-border pt-3 text-sm">
      <p>{t("goal_assessment.reference_hours", { from: number(example.weekly_hours_from), to: number(example.weekly_hours_to) })}</p>
      <p className="mt-1 text-taupe">{t("goal_assessment.reference_note")}</p>
      {source && <a className="mt-1 inline-block underline" href={source} target="_blank" rel="noopener noreferrer">{t("goal_assessment.reference_source")}</a>}
    </div>}
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
      <Link className="underline" to="/settings#events">{t("goal_assessment.edit_calendar")}</Link>
      <Link className="underline" to="/settings?edit=training#training-profile">{t("goal_assessment.edit_training")}</Link>
    </div>
  </Card>;
}
